import { useCallback, useEffect, useRef, useState } from 'react';
import { getHotelApiToken } from '../../../core/auth/getHotelApiToken';
import type { DestinationApiResponse, DestinationSuggestion } from '../api/hotels.types';
import { destinationsUrl } from '../api/hotels.endpoints';

/**
 * Status of the destination search.
 *
 * - idle: no search in progress (short term, empty results, or initial state)
 * - loading: fetch is in flight
 * - error: fetch failed (network, non-200, or malformed response)
 */
export type DestinationSearchStatus = 'idle' | 'loading' | 'error';

export interface UseDestinationSearchResult {
  suggestions: DestinationSuggestion[];
  status: DestinationSearchStatus;
  /** Human-readable error detail, null when status !== 'error'. */
  error: string | null;
}

interface UseDestinationSearchOptions {
  /** Minimum characters before fetching. Default: 3. */
  minChars?: number;
  /** Debounce delay in milliseconds. Default: 250. */
  debounceMs?: number;
  /** Maximum suggestions to request from the API. Default: 10. */
  limit?: number;
  /**
   * Custom fetch implementation for testing or proxying.
   * Must satisfy the global `fetch` signature for URL + RequestInit.
   */
  fetchFn?: typeof fetch;
  /** Custom token resolver for tests. Defaults to the Package Suppliers token flow. */
  getTokenFn?: typeof getHotelApiToken;
}

const DEFAULT_MIN_CHARS = 3;
const DEFAULT_DEBOUNCE_MS = 250;
const DEFAULT_LIMIT = 10;

function resolveDestinationName(destination: DestinationApiResponse): string {
  if (typeof destination.name === 'string') return destination.name;

  return (
    destination.name.es ||
    destination.name.en ||
    destination.name.pt ||
    Object.values(destination.name).find((value): value is string => Boolean(value)) ||
    destination.id
  );
}

function normalizeDestination(destination: DestinationApiResponse): DestinationSuggestion {
  return {
    id: destination.id,
    name: resolveDestinationName(destination),
    countryCode: destination.countryCode,
    countryName: destination.countryName,
  };
}

/**
 * Debounced destination search hook.
 *
 * Fetches destination suggestions from the real API behind
 * `destinationsUrl(search, limit)`. Guards against stale responses
 * via `AbortController` + monotonic search-id comparison.
 *
 * Runtime hotel destinations are API-backed. Tests may inject fetch/token
 * dependencies, but product code must not fall back to mock suggestions.
 */
export function useDestinationSearch(
  term: string,
  options?: UseDestinationSearchOptions,
): UseDestinationSearchResult {
  const { minChars, debounceMs, limit, fetchFn, getTokenFn } = options ?? {};
  const resolvedMinChars = minChars ?? DEFAULT_MIN_CHARS;
  const resolvedDebounceMs = debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const resolvedLimit = limit ?? DEFAULT_LIMIT;

  const [suggestions, setSuggestions] = useState<DestinationSuggestion[]>([]);
  const [status, setStatus] = useState<DestinationSearchStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const controllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchRef = useRef(fetchFn);
  const tokenRef = useRef(getTokenFn);

  // Keep fetchFn current without triggering effect restarts.
  fetchRef.current = fetchFn;
  tokenRef.current = getTokenFn;

  // Stable fetch wrapper that always uses the latest fetchFn
  const doFetch = useCallback((url: string, init: RequestInit) => {
    const fetcher = fetchRef.current ?? fetch;
    return fetcher(url, init);
  }, []);

  useEffect(() => {
    // Short terms: reset and skip fetch.
    if (term.length < resolvedMinChars) {
      // Abort any in-flight request.
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setSuggestions([]);
      setStatus('idle');
      setError(null);
      return;
    }

    // Clear any pending debounce timer.
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    let cancelled = false;
    const searchId = Symbol('search');

    timerRef.current = setTimeout(async () => {
      // Abort any previous in-flight fetch.
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;

      if (cancelled) return;

      setStatus('loading');
      setError(null);

      try {
        const url = destinationsUrl(term, resolvedLimit);
        const tokenResolver = tokenRef.current ?? getHotelApiToken;
        const token = tokenRef.current || !fetchRef.current ? await tokenResolver() : null;

        if (cancelled || controller.signal.aborted) return;

        const response = await doFetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: controller.signal,
        });

        // Guard: abort or newer search started.
        if (cancelled || controller.signal.aborted) return;

        if (!response.ok) {
          throw new Error(`destinations_search_failed:${response.status}`);
        }

        const data: DestinationApiResponse[] = await response.json();

        // Guard again after async JSON parse.
        if (cancelled || controller.signal.aborted) return;

        setSuggestions(data.map(normalizeDestination));
        setStatus('idle');
      } catch (err: unknown) {
        // Silently ignore abort errors — they are intentional.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (cancelled) return;

        setSuggestions([]);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'hotels.destinations.unknownError');
      }
    }, resolvedDebounceMs);

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [term, resolvedMinChars, resolvedDebounceMs, resolvedLimit, doFetch]);

  // Cleanup on unmount: abort any in-flight request.
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
  }, []);

  return { suggestions, status, error };
}
